import { CouponActInfoWithGoodsAndState } from '@tencent/wphk-act-base-framework';
import { CouponState } from '@tencent/wphk-act-base-types';
import { DetailButtonType } from '@tencent/wphk-act-base-ui';
import { computed } from 'vue';
import { PayActItem, PayActType } from '../business/pay-act';
import { COUPON_STAMP_TEXT, DETAIL_DIALOG_BTN_TEXT } from '../config';
import { ExchButtonStatus } from '@/types/exch-info';
import { useExchInfo } from '@/hooks/use-exch-info';
import { TaskReceiveStatus, useTaskInfo } from './use-task-info';
import { useActConfigStore } from '../../../store/act-config';

const formatExchStatus = (exchActInfo: CouponActInfoWithGoodsAndState) => {
  if ([CouponState.expired, CouponState.received, CouponState.used].includes(exchActInfo.couponState!)) {
    return TaskReceiveStatus.Received;
  }
  return TaskReceiveStatus.notReceive;
};

/** 格式化详情弹窗中按钮状态 */
const formatDetailButtonStatus = (payActItem: PayActItem): ExchButtonStatus => {
  const { taskReceiveStatus } = formatPayActItemInfo(payActItem);
  const getIsToReceive = (taskReceiveStatus: TaskReceiveStatus) => taskReceiveStatus === TaskReceiveStatus.notReceive;
  const getIsTaskLocked = (taskReceiveStatus: TaskReceiveStatus) => [
    TaskReceiveStatus.notComplete,
    TaskReceiveStatus.notJoin,
  ].includes(taskReceiveStatus);
  const { exchButtonStatus } = useExchInfo({
    exchInfo: payActItem.exchInfo,
    extraInfo: {
      isLocked: getIsTaskLocked(taskReceiveStatus),
      isToReceive: getIsToReceive(taskReceiveStatus),
    },
  });
  return exchButtonStatus;
};

const formatPayActItemInfo = (payActItem: PayActItem) => {
  if (payActItem.actType === PayActType.task) {
    const taskInfo = useTaskInfo(payActItem.taskInfo!);
    // 如果任务未领取/未完成，则以任务状态为准
    if ([TaskReceiveStatus.notComplete, TaskReceiveStatus.notJoin].includes(taskInfo.taskReceiveStatus)) {
      return taskInfo;
    }
    return {
      ...taskInfo,
      taskReceiveStatus: formatExchStatus(payActItem.exchInfo),
    };
  }
  return {
    taskReceiveStatus: formatExchStatus(payActItem.exchInfo),
    needCount: 0,
    requiredCount: 0,
  };
};

const getButtonStatus = (actItem: PayActItem) => formatDetailButtonStatus(actItem);
const getIsShowBtn = (buttonStatus: ExchButtonStatus) => buttonStatus !== ExchButtonStatus.Hidden;
const getIsDisabled = (buttonStatus: ExchButtonStatus) => [
  ExchButtonStatus.IsLocked, ExchButtonStatus.IsLimit,
].includes(buttonStatus);
const getButtonType = ({ buttonStatus, isDisabled }: { buttonStatus: ExchButtonStatus, isDisabled: boolean }) => {
  if (buttonStatus === ExchButtonStatus.ToUse) {
    return DetailButtonType.ghost;
  }
  if (isDisabled) {
    return DetailButtonType.outofperiod;
  }
  return DetailButtonType.primary;
};
const getBtnBottomInfo = ({ buttonStatus, requiredCount }:
{ buttonStatus: ExchButtonStatus, requiredCount: number }) => {
  const { targetActConfig } = useActConfigStore();
  if (buttonStatus === ExchButtonStatus.IsLocked) {
    return targetActConfig?.payTaskModule.btnBottomInfo.replace('${x}', `${requiredCount}`);
  } if (buttonStatus === ExchButtonStatus.IsLimit) {
    return '今日已領完，明日再來拎！';
  }
};

/** 任务是否差一笔就完成 */
const getIsActItemInProcess = (actItem: PayActItem) => {
  const { needCount, taskReceiveStatus } = formatPayActItemInfo(actItem);
  return TaskReceiveStatus.notComplete === taskReceiveStatus && needCount === 1;
};

export const usePayActItem = (payActItem: PayActItem) => {
  const { needCount, requiredCount, taskReceiveStatus } = formatPayActItemInfo(payActItem);
  const buttonStatus = getButtonStatus(payActItem);
  const isDisabled = getIsDisabled(buttonStatus);
  const couponDetailExtraInfo = computed(() => ({
    stampText: COUPON_STAMP_TEXT[payActItem.exchInfo.couponState!],
    isExpire: buttonStatus === ExchButtonStatus.Hidden,
  }));
  return {
    btnBottomInfo: getBtnBottomInfo({ buttonStatus, requiredCount }),
    btnInfo: {
      isShowBtn: getIsShowBtn(buttonStatus),
      isDisabled,
      btnText: DETAIL_DIALOG_BTN_TEXT[buttonStatus],
      type: getButtonType({ isDisabled, buttonStatus }),
    },
    buttonStatus,
    needCount,
    requiredCount,
    taskReceiveStatus,
    couponDetailExtraInfo,
    isActItemInProcess: getIsActItemInProcess(payActItem),
  };
};

export const usePayActList = (actItems: PayActItem[]) => {
  const getInProcessTasks = () => actItems.filter((item) => {
    const { taskReceiveStatus } = formatPayActItemInfo(item);
    const isCompleted = [TaskReceiveStatus.Received, TaskReceiveStatus.notReceive].includes(taskReceiveStatus);
    return isCompleted;
  });
  const getIsFirstTaskReceive = () => {
    const firstTask = actItems[0];
    const { taskReceiveStatus } = formatPayActItemInfo(firstTask);
    return taskReceiveStatus === TaskReceiveStatus.Received;
  };
  return {
    inProcessTasks: getInProcessTasks(),
    isFirstTaskReceive: getIsFirstTaskReceive(),
  };
};
